import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Autocomplete,
    Chip,
    Button,
    Card,
    CardContent,
    Alert,
    AlertTitle,
    List,
    ListItem,
    ListItemText,
    Divider,
    CircularProgress,
    Grid,
    InputAdornment
} from '@mui/material';
import {
    LiveTv as LiveTvIcon,
    Search as SearchIcon,
    AttachMoney as MoneyIcon,
    TrendingUp as TrendingUpIcon,
    CheckCircle as CheckIcon,
    Info as InfoIcon,
    NewReleases as NewIcon
} from '@mui/icons-material';
import axios from 'axios';

function TvPackageRecommenderV2() {
    const [loading, setLoading] = useState(true);
    const [channels, setChannels] = useState([]);
    const [pricing, setPricing] = useState({});
    const [selectedChannels, setSelectedChannels] = useState([]);
    const [recommendation, setRecommendation] = useState(null);
    const [calculating, setCalculating] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [channelsRes, pricingRes] = await Promise.all([
                axios.get('/api/tv-packages-v2/channels'),
                axios.get('/api/tv-packages-v2/pricing')
            ]);
            
            setChannels(channelsRes.data.channels || []);
            setPricing(pricingRes.data || {});
        } catch (error) {
            console.error('Error fetching V2 data:', error);
        }
        setLoading(false);
    };

    const handleCalculateRecommendation = async () => {
        if (selectedChannels.length === 0) {
            return;
        }

        setCalculating(true);
        try {
            const response = await axios.post('/api/tv-packages-v2/recommend', {
                selectedChannels
            });
            setRecommendation(response.data.recommendation);
        } catch (error) {
            console.error('Error calculating V2 recommendation:', error);
        }
        setCalculating(false);
    };

    const formatPrice = (price) => {
        return `$${parseFloat(price).toFixed(2)}/month`;
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <LiveTvIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
                TV Package Calculator V2
                <NewIcon sx={{ ml: 2, color: 'secondary.main' }} />
            </Typography>

            {/* V2 Features Alert */}
            <Alert severity="info" sx={{ mb: 3 }}>
                <AlertTitle>New V2 Features</AlertTitle>
                <Typography variant="body2">
                    • Improved channel search with proper display names (A&E, T+E, etc.)
                    • Added missing channels: Xbox Game Pass, GMA Pinoy TV, Paramount+
                    • Better search functionality with multiple aliases
                    • Enhanced channel management for administrators
                </Typography>
            </Alert>

            {/* Search and Selection */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Select Channels or Theme Packs
                </Typography>
                
                <Autocomplete
                    multiple
                    options={channels}
                    value={selectedChannels}
                    onChange={(event, newValue) => {
                        // Check for conflicts
                        const lastAdded = newValue[newValue.length - 1];
                        if (lastAdded) {
                            // If adding a theme pack, check for individual channels
                            if (lastAdded.type === 'theme-pack') {
                                const conflictingChannels = selectedChannels.filter(
                                    ch => ch.type === 'channel' && 
                                    lastAdded.channels?.some(packCh => 
                                        packCh.toLowerCase() === ch.name.toLowerCase()
                                    )
                                );
                                if (conflictingChannels.length > 0) {
                                    alert(`Cannot add this theme pack. You've already selected individual channels from it: ${conflictingChannels.map(ch => ch.name).join(', ')}`);
                                    return;
                                }
                            }
                            // If adding an individual channel, check for theme packs
                            if (lastAdded.type === 'channel') {
                                const conflictingPacks = selectedChannels.filter(
                                    ch => ch.type === 'theme-pack' && 
                                    ch.channels?.some(packCh => 
                                        packCh.toLowerCase() === lastAdded.name.toLowerCase()
                                    )
                                );
                                if (conflictingPacks.length > 0) {
                                    alert(`This channel is already included in the selected theme pack: ${conflictingPacks[0].name}`);
                                    return;
                                }
                            }
                        }
                        setSelectedChannels(newValue);
                        setRecommendation(null); // Clear previous recommendation
                    }}
                    getOptionLabel={(option) => option.name}
                    groupBy={(option) => {
                        if (option.type === 'theme-pack') return 'Theme Packs';
                        if (option.type === 'premium') return 'Premium Channels';
                        if (option.type === 'core') return 'Core Channels';
                        return 'Theme Pack Channels';
                    }}
                    renderOption={(props, option) => (
                        <Box component="li" {...props}>
                            <Box sx={{ width: '100%' }}>
                                <Typography variant="body1">{option.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {option.type === 'theme-pack' ? 'Theme Pack' : option.type}
                                    {option.packs && option.packs.length > 0 && ` | ${option.packs.join(', ')}`}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                variant="outlined"
                                label={option.name}
                                color={
                                    option.type === 'premium' ? 'secondary' :
                                    option.type === 'theme-pack' ? 'primary' : 'default'
                                }
                                size="small"
                                {...getTagProps({ index })}
                            />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Search for channels or theme packs..."
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <>
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                        {params.InputProps.startAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    sx={{ mb: 3 }}
                />

                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    onClick={handleCalculateRecommendation}
                    disabled={selectedChannels.length === 0 || calculating}
                    startIcon={calculating ? <CircularProgress size={20} /> : <MoneyIcon />}
                >
                    {calculating ? 'Calculating...' : 'Get Recommendation'}
                </Button>
            </Paper>

            {/* Recommendation Results */}
            {recommendation && (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Alert severity="info" variant="outlined">
                            Use Bundle Builder Beta to send customer quotes. TV Package Calculator V2 is recommendation-only.
                        </Alert>
                    </Grid>

                    {/* Summary */}
                    {(recommendation?.requiredThemePacks?.length > 0 || recommendation?.premiumChannels?.length > 0) && (
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Selection Summary
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {recommendation?.requiredThemePacks?.length > 0 && (
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                    Required Theme Packs ({recommendation?.requiredThemePacks?.length || 0})
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    {(recommendation?.requiredThemePacks || []).map((pack, idx) => (
                                                        <Chip key={idx} label={pack} size="small" color="primary" />
                                                    ))}
                                                </Box>
                                            </Grid>
                                        )}
                                        {recommendation?.premiumChannels?.length > 0 && (
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                    Premium Channels ({recommendation?.premiumChannels?.length || 0})
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    {(recommendation?.premiumChannels || []).map((channel, idx) => (
                                                        <Chip key={idx} label={channel} size="small" color="secondary" />
                                                    ))}
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* Best Value Option */}
                    {recommendation.bestValue && (
                        <Grid item xs={12} md={recommendation.alternative || recommendation.upsell ? 6 : 12}>
                            <Card sx={{ height: '100%', border: '2px solid', borderColor: 'success.main' }}>
                                <CardContent>
                                    <Alert severity="success" sx={{ mb: 2 }}>
                                        <AlertTitle sx={{ display: 'flex', alignItems: 'center' }}>
                                            <CheckIcon sx={{ mr: 1 }} />
                                            Best Value Package
                                        </AlertTitle>
                                    </Alert>
                                    
                                    <Typography variant="h5" gutterBottom>
                                        {recommendation.bestValue.pkgStr}
                                    </Typography>
                                    
                                    <List dense>
                                        <ListItem>
                                            <ListItemText 
                                                primary="Base Price"
                                                secondary={formatPrice(recommendation.bestValue.basePrice)}
                                            />
                                        </ListItem>
                                        
                                        {recommendation.bestValue.addOnCount > 0 && (
                                            <ListItem>
                                                <ListItemText 
                                                    primary={`Add-on: ${recommendation.bestValue.addOnCount} extra theme pack${recommendation.bestValue.addOnCount > 1 ? 's' : ''}`}
                                                    secondary={`+ ${formatPrice(recommendation.bestValue.addOnTotal)}`}
                                                />
                                            </ListItem>
                                        )}
                                        
                                        <Divider />
                                        
                                        <ListItem>
                                            <ListItemText 
                                                primary={<Typography variant="h6">Total Price</Typography>}
                                                secondary={
                                                    <Typography variant="h5" color="primary">
                                                        {formatPrice(recommendation.bestValue.total)}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                    </List>
                                    
                                    {recommendation.bestValue.note && (
                                        <Alert severity="info" sx={{ mt: 2 }}>
                                            {recommendation.bestValue.note}
                                        </Alert>
                                    )}
                                    
                                    {(recommendation.bestValue.extraThemes > 0 || recommendation.bestValue.extraPremiums > 0) && (
                                        <Alert severity="info" sx={{ mt: 2 }}>
                                            <AlertTitle>Bonus Included</AlertTitle>
                                            {recommendation.bestValue.extraThemes > 0 && (
                                                <Typography variant="body2">
                                                    • {recommendation.bestValue.extraThemes} extra theme pack{recommendation.bestValue.extraThemes > 1 ? 's' : ''} of your choice
                                                </Typography>
                                            )}
                                            {recommendation.bestValue.extraPremiums > 0 && (
                                                <Typography variant="body2">
                                                    • {recommendation.bestValue.extraPremiums} extra premium channel{recommendation.bestValue.extraPremiums > 1 ? 's' : ''} of your choice
                                                </Typography>
                                            )}
                                        </Alert>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* Alternative Option */}
                    {recommendation.alternative && (
                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        <AlertTitle>Alternative Package</AlertTitle>
                                    </Alert>
                                    
                                    <Typography variant="h5" gutterBottom>
                                        {recommendation.alternative.pkgStr}
                                    </Typography>
                                    
                                    <List dense>
                                        <ListItem>
                                            <ListItemText 
                                                primary="Base Price"
                                                secondary={formatPrice(recommendation.alternative.basePrice)}
                                            />
                                        </ListItem>
                                        
                                        {recommendation.alternative.addOnCount > 0 && (
                                            <ListItem>
                                                <ListItemText 
                                                    primary={`Add-on: ${recommendation.alternative.addOnCount} extra theme pack${recommendation.alternative.addOnCount > 1 ? 's' : ''}`}
                                                    secondary={`+ ${formatPrice(recommendation.alternative.addOnTotal)}`}
                                                />
                                            </ListItem>
                                        )}
                                        
                                        <Divider />
                                        
                                        <ListItem>
                                            <ListItemText 
                                                primary={<Typography variant="h6">Total Price</Typography>}
                                                secondary={
                                                    <Typography variant="h5" color="primary">
                                                        {formatPrice(recommendation.alternative.total)}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                    </List>
                                    
                                    {recommendation.alternative.note && (
                                        <Alert severity="info" sx={{ mt: 2 }}>
                                            {recommendation.alternative.note}
                                        </Alert>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* Upsell Option */}
                    {recommendation.upsell && recommendation.upsell.priceDiff > 0 && (
                        <Grid item xs={12}>
                            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                                <CardContent>
                                    <Alert 
                                        severity="info" 
                                        sx={{ 
                                            mb: 2,
                                            backgroundColor: 'rgba(255,255,255,0.9)'
                                        }}
                                    >
                                        <AlertTitle sx={{ display: 'flex', alignItems: 'center' }}>
                                            <TrendingUpIcon sx={{ mr: 1 }} />
                                            Upgrade Opportunity
                                        </AlertTitle>
                                    </Alert>
                                    
                                    <Typography variant="h5" gutterBottom sx={{ color: 'white' }}>
                                        {recommendation.upsell.pkgStr}
                                    </Typography>
                                    
                                    <Typography variant="h4" sx={{ color: 'white', mb: 2 }}>
                                        {formatPrice(recommendation.upsell.price)}
                                    </Typography>
                                    
                                    <Alert severity="success" sx={{ backgroundColor: 'rgba(255,255,255,0.9)' }}>
                                        <Typography variant="body1">
                                            For just <strong>{formatPrice(recommendation.upsell.priceDiff || 0)}</strong> more, you get:
                                        </Typography>
                                        <Box sx={{ mt: 1 }}>
                                            {recommendation.upsell.extraThemes > 0 && (
                                                <Typography variant="body2">
                                                    • {recommendation.upsell.extraThemes} extra theme pack{recommendation.upsell.extraThemes > 1 ? 's' : ''}
                                                </Typography>
                                            )}
                                            {recommendation.upsell.extraPremiums > 0 && (
                                                <Typography variant="body2">
                                                    • {recommendation.upsell.extraPremiums} extra premium channel{recommendation.upsell.extraPremiums > 1 ? 's' : ''}
                                                </Typography>
                                            )}
                                            {(!recommendation.upsell.extraThemes || recommendation.upsell.extraThemes === 0) && 
                                             (!recommendation.upsell.extraPremiums || recommendation.upsell.extraPremiums === 0) && (
                                                <Typography variant="body2">
                                                    • Better value for your selection
                                                </Typography>
                                            )}
                                        </Box>
                                    </Alert>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                </Grid>
            )}

        </Box>
    );
}

export default TvPackageRecommenderV2;
